import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import {
  pageSectionSubtitleSx,
  pageSectionTitleSx,
} from "../../../constants/pageSectionStyles";
import { brandContainedButtonSx } from "../../../constants/brandContainedButtonStyles";
import { useBrandThemeColors } from "../../../branding";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useHasPrivilegeStatus } from "../../../hooks/useHasPrivilege";
import { useAcTenantId } from "../../../hooks/useAcTenantId";
import { useAutoTaggingPlanTypes } from "../../../hooks/useAutoTaggingPlanTypes";
import {
  formatConceptCodeToLabel,
  importAutoTaggingPlansFromFile,
  submitAssessmentPlanItems,
} from "../../../services/autoTaggingService";
import { AUTO_TAGGING_PRIVILEGES } from "../../../services/permissionsService";
import type { ConceptValue } from "../../../services/regulationsService";
import { useRegulationsNextShellIntegration } from "../../shared/useRegulationsNextShellIntegration";
import arrowLeftIcon from "../../../assets/icon-left.svg";
import AssessmentPlanFilterFields from "./AssessmentPlanFilterFields";
import AssessmentPromptFieldWithDefault from "./AssessmentPromptFieldWithDefault";
import { ASSESSMENT_PROMPT_MAX_LENGTH } from "./AssessmentPromptField";
import DefaultAssessmentPromptPrefetch from "./DefaultAssessmentPromptPrefetch";
import ConceptValuesMultiSelect from "./ConceptValuesMultiSelect";
import ExtractFromFileDialog from "./ExtractFromFileDialog";
import {
  areAllComboStatsLoaded,
  buildPlanLimitToastMessage,
  collectExceededConceptLimits,
  collectPlanLimitClearItemIds,
  isConceptComboSelectionPatch,
  isConceptPlanLimitError,
  validateImportedFormItems,
  type ConceptComboDraftItem,
  type ConceptPlanLimitItem,
} from "./conceptStatsValidation";
import { useDraftConceptStats } from "./useDraftConceptStats";
import { AI_PLAN_PATH } from "../constants";
import {
  hasDraftItemFieldErrors,
  PLAN_LIMIT_CONCEPT_ERROR,
  validateDraftItem,
  type DraftItemFieldErrors,
  type DraftItemFieldKey,
} from "./validateDraftItem";

const sectionLabelSx = {
  fontSize: "0.875rem",
  fontWeight: 500,
  color: "#6b7280",
  mb: 1,
};

type DraftItem = {
  id: string;
  objectType: string;
  objectSubType: string;
  conceptCode: string;
  conceptValues: ConceptValue[];
  assessmentPrompt: string;
  allValues?: boolean;
};

let draftItemIdCounter = 0;

function createEmptyDraftItem(defaults?: {
  objectType: string;
  objectSubType: string;
}): DraftItem {
  draftItemIdCounter += 1;
  return {
    id: `draft-item-${draftItemIdCounter}`,
    objectType: defaults?.objectType ?? "",
    objectSubType: defaults?.objectSubType ?? "",
    conceptCode: "",
    conceptValues: [],
    assessmentPrompt: "",
    allValues: false,
  };
}

function createDraftItemFromImport(
  imported: Awaited<ReturnType<typeof importAutoTaggingPlansFromFile>>[number],
): DraftItem {
  draftItemIdCounter += 1;
  return {
    id: `draft-item-${draftItemIdCounter}`,
    objectType: imported.objectType,
    objectSubType: imported.objectSubType,
    conceptCode: imported.conceptCode,
    conceptValues: imported.conceptValues,
    assessmentPrompt: imported.assessmentPrompt,
    allValues: imported.allValues,
  };
}

export default function AddAssessmentPlanItemsPage() {
  const acTenantId = useAcTenantId();
  return (
    <AddAssessmentPlanItemsPageContent key={acTenantId || "no-ac-tenant"} />
  );
}

function AddAssessmentPlanItemsPageContent() {
  useRegulationsNextShellIntegration();
  const navigate = useNavigate();
  const { primaryColor, primaryFontColor } = useBrandThemeColors();
  const { showMessage } = useSnackbar();
  const { has: canManageAutoTagging, loaded: privilegesLoaded } =
    useHasPrivilegeStatus(AUTO_TAGGING_PRIVILEGES.manage);
  const {
    defaultObjectType,
    getSubTypesForObjectType,
  } = useAutoTaggingPlanTypes();

  const [draftItems, setDraftItems] = useState<DraftItem[]>(() => [
    createEmptyDraftItem(),
  ]);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [importing, setImporting] = useState(false);
  const [extractDialogOpen, setExtractDialogOpen] = useState(false);
  const [fieldErrorsByItemId, setFieldErrorsByItemId] = useState<
    Record<string, DraftItemFieldErrors>
  >({});

  const handlePlanLimitReached = useCallback(
    (
      items: ConceptPlanLimitItem[],
      itemsSnapshot?: readonly DraftItem[],
      toastMessage?: string,
      clearOptions?: {
        onlyItemId?: string;
        availableByComboKey?: Readonly<Record<string, number>>;
      },
    ) => {
      if (items.length === 0) return;

      showMessage(toastMessage ?? buildPlanLimitToastMessage(items), "warning");

      const sourceItems = itemsSnapshot ?? draftItems;
      const clearIds = new Set(
        collectPlanLimitClearItemIds(
          sourceItems,
          items,
          defaultObjectType,
          clearOptions,
        ),
      );
      if (clearIds.size === 0) return;

      setDraftItems(
        sourceItems.map((item) =>
          clearIds.has(item.id)
            ? {
                ...item,
                conceptCode: "",
                conceptValues: [],
                assessmentPrompt: "",
                allValues: false,
              }
            : item,
        ),
      );

      setFieldErrorsByItemId((prev) => {
        const next = { ...prev };
        for (const id of clearIds) {
          next[id] = {
            ...next[id],
            conceptCode: PLAN_LIMIT_CONCEPT_ERROR,
          };
        }
        return next;
      });
    },
    [defaultObjectType, draftItems, showMessage],
  );

  const { statsByComboKey, assertAvailability } = useDraftConceptStats(
    draftItems,
    defaultObjectType,
    showMessage,
    handlePlanLimitReached as (
      items: ConceptPlanLimitItem[],
      itemsSnapshot?: readonly ConceptComboDraftItem[],
      toastMessage?: string,
      clearOptions?: {
        onlyItemId?: string;
        availableByComboKey?: Readonly<Record<string, number>>;
      },
    ) => void,
  );

  const outlinedPrimarySx = {
    textTransform: "none",
    fontWeight: 500,
    borderColor: primaryColor,
    color: primaryColor,
    "&:hover": {
      borderColor: primaryColor,
      backgroundColor: "rgba(37, 99, 235, 0.04)",
    },
    "& .MuiButton-startIcon .MuiSvgIcon-root": {
      color: primaryColor,
    },
  } as const;

  const handleUploadClick = useCallback(() => {
    setExtractDialogOpen(true);
  }, []);

  const handleCloseExtractDialog = useCallback(() => {
    if (importing) return;
    setExtractDialogOpen(false);
  }, [importing]);

  const handleConfirmExtractFile = useCallback(
    async (file: File) => {
      setImporting(true);
      try {
        const importedItems = await importAutoTaggingPlansFromFile(file);
        const importValidationMessage = validateImportedFormItems(
          importedItems,
          {
            requiresObjectSubType: (objectType) =>
              getSubTypesForObjectType(objectType).length > 0,
          },
        );
        if (importValidationMessage) {
          throw new Error(importValidationMessage);
        }
        const nextDraftItems = importedItems.map(createDraftItemFromImport);
        await assertAvailability(nextDraftItems);
        setFieldErrorsByItemId({});
        setDraftItems(nextDraftItems);
        setExtractDialogOpen(false);
        showMessage(
          `${importedItems.length} item(s) imported from file.`,
          "success",
        );
      } catch (error) {
        if (isConceptPlanLimitError(error)) {
          showMessage(error.message, "warning");
        } else {
          showMessage(
            error instanceof Error
              ? error.message
              : "Failed to import assessment plan items.",
            "error",
          );
        }
      } finally {
        setImporting(false);
      }
    },
    [assertAvailability, getSubTypesForObjectType, showMessage],
  );

  const handleAddOneItem = useCallback(() => {
    setDraftItems((prev) => [...prev, createEmptyDraftItem()]);
  }, []);

  const handleRemoveItem = useCallback((id: string) => {
    setDraftItems((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((item) => item.id !== id);
    });
  }, []);

  const clearFieldErrors = useCallback(
    (id: string, fields: DraftItemFieldKey[]) => {
      setFieldErrorsByItemId((prev) => {
        const itemErrors = prev[id];
        if (!itemErrors) return prev;

        const nextItemErrors = { ...itemErrors };
        fields.forEach((field) => {
          delete nextItemErrors[field];
        });

        if (Object.keys(nextItemErrors).length === 0) {
          const next = { ...prev };
          delete next[id];
          return next;
        }

        return { ...prev, [id]: nextItemErrors };
      });
    },
    [],
  );

  const handleUpdateItem = useCallback(
    (
      id: string,
      patch: Partial<
        Pick<
          DraftItem,
          | "objectType"
          | "objectSubType"
          | "conceptCode"
          | "conceptValues"
          | "assessmentPrompt"
          | "allValues"
        >
      >,
    ) => {
      const clearedFields = Object.keys(patch) as DraftItemFieldKey[];
      if (patch.conceptCode !== undefined) {
        clearedFields.push("conceptValues", "assessmentPrompt");
      }
      if (patch.conceptValues !== undefined || patch.allValues !== undefined) {
        clearedFields.push("assessmentPrompt");
      }
      if (patch.objectType !== undefined || patch.objectSubType !== undefined) {
        clearedFields.push("conceptCode", "conceptValues", "assessmentPrompt");
      }
      clearFieldErrors(id, clearedFields);

      // Functional update avoids stale draftItems overwriting a prior type change
      // (e.g. Insight → RTQ racing with concept-value name hydration).
      setDraftItems((prev) => {
        const nextItems = prev.map((item) => {
          if (item.id !== id) return item;

          const next: DraftItem = { ...item, ...patch };
          if (
            patch.objectType !== undefined ||
            patch.objectSubType !== undefined
          ) {
            // Match edit dialog: changing type/sub-type resets dependent fields.
            next.conceptCode = "";
            next.conceptValues = [];
            next.assessmentPrompt = "";
            next.allValues = false;
          }
          return next;
        });

        if (isConceptComboSelectionPatch(patch)) {
          const exceeded = collectExceededConceptLimits(
            nextItems,
            statsByComboKey,
            defaultObjectType,
          );
          const allStatsReady = areAllComboStatsLoaded(
            nextItems,
            statsByComboKey,
            defaultObjectType,
          );
          if (allStatsReady && exceeded.length > 0) {
            // Defer so we don't nest another setDraftItems inside this updater.
            queueMicrotask(() => {
              handlePlanLimitReached(exceeded, nextItems, undefined, {
                onlyItemId: id,
              });
            });
            return prev;
          }
        }

        return nextItems;
      });
    },
    [
      clearFieldErrors,
      defaultObjectType,
      handlePlanLimitReached,
      statsByComboKey,
    ],
  );

  const handleCancel = useCallback(() => {
    navigate(AI_PLAN_PATH);
  }, [navigate]);

  const handleSubmit = useCallback(async () => {
    // Ref guard blocks double-submit before React re-renders `submitting`.
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);

    const nextFieldErrors: Record<string, DraftItemFieldErrors> = {};
    const validItems: DraftItem[] = [];

    try {
      for (const item of draftItems) {
        const assessmentPrompt = item.assessmentPrompt.trim();
        const itemErrors = validateDraftItem(
          {
            ...item,
            assessmentPrompt,
            allValues: item.allValues ?? false,
          },
          {
            requireObjectSubType:
              getSubTypesForObjectType(item.objectType).length > 0,
          },
        );
        if (hasDraftItemFieldErrors(itemErrors)) {
          nextFieldErrors[item.id] = itemErrors;
        } else {
          validItems.push({ ...item, assessmentPrompt });
        }
      }

      if (validItems.length === 0 || Object.keys(nextFieldErrors).length > 0) {
        setFieldErrorsByItemId(nextFieldErrors);
        return;
      }

      setFieldErrorsByItemId({});
      await assertAvailability(validItems);
      await submitAssessmentPlanItems(
        validItems.map((item) => ({
          ...item,
          conceptValues: item.allValues ? [] : item.conceptValues,
          allValues: item.allValues ?? false,
        })),
      );
      showMessage(
        `${validItems.length} item(s) submitted to the global plan.`,
        "success",
      );
      navigate(AI_PLAN_PATH);
    } catch (error) {
      if (isConceptPlanLimitError(error)) {
        handlePlanLimitReached(error.items, draftItems, error.message);
      } else {
        showMessage(
          error instanceof Error
            ? error.message
            : "Failed to submit assessment plan items.",
          "error",
        );
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [
    assertAvailability,
    draftItems,
    getSubTypesForObjectType,
    handlePlanLimitReached,
    navigate,
    showMessage,
  ]);

  if (privilegesLoaded && !canManageAutoTagging) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: 1,
          px: 3,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Access Denied
        </Typography>
        <Typography variant="body2" color="text.secondary">
          You do not have permission to manage Tagging Plan items.
        </Typography>
        <Button onClick={() => navigate(AI_PLAN_PATH)} sx={{ mt: 1 }}>
          Back to Organisational AI Plan
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          py: "16px",
          px: "24px",
          flexShrink: 0,
          bgcolor: "#fff",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
          }}
        >
          <IconButton
            aria-label="Go back"
            onClick={handleCancel}
            sx={{
              width: 32,
              height: 32,
              p: 1,
              flexShrink: 0,
              mt: 0.25,
            }}
          >
            <Box
              component="img"
              src={arrowLeftIcon}
              alt=""
              sx={{
                width: 16.25,
                height: 16,
                display: "block",
                objectFit: "contain",
              }}
            />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Breadcrumbs
              separator="›"
              sx={{
                mb: "4px",
                "& .MuiBreadcrumbs-separator": { color: "#9ca3af", mx: 1 },
              }}
            >
              <Link
                component="button"
                variant="body1"
                onClick={handleCancel}
                sx={{
                  color: primaryColor,
                  textDecoration: "none",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Organisational AI Plan
              </Link>
              <Typography
                sx={{
                  color: "#374151",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                Add New Assessment Plan Items
              </Typography>
            </Breadcrumbs>
            <Typography sx={{ ...pageSectionTitleSx, mb: "4px" }}>
              Add New Assessment Plan Items
            </Typography>
            <Typography sx={pageSectionSubtitleSx}>
              Upload a file or manually add assessment items. Review concepts,
              values, and prompts before submitting to the global plan.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            bgcolor: "#F9FAFB",
            px: "24px",
            pt: "24px",
            pb: "24px",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 800,
              bgcolor: "#fff",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
              p: "24px",
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <Box>
              <Typography sx={sectionLabelSx}>Extract From File</Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileUploadOutlinedIcon sx={{ fontSize: 18 }} />}
                onClick={handleUploadClick}
                disabled={submitting}
                sx={{
                  ...outlinedPrimarySx,
                  height: 32,
                  minHeight: 32,
                  px: 1.5,
                  borderRadius: "6px",
                }}
              >
                Upload File
              </Button>
            </Box>

            {draftItems.map((item, index) => {
              const fieldErrors = fieldErrorsByItemId[item.id];
              const showConceptValues =
                Boolean(item.conceptCode) || Boolean(fieldErrors?.conceptValues);
              const showAssessmentPrompt =
                item.conceptValues.length > 0 ||
                item.allValues ||
                Boolean(fieldErrors?.assessmentPrompt);
              return (
              <Box
                key={item.id}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  pb: index < draftItems.length - 1 ? 3 : 0,
                  borderBottom:
                    index < draftItems.length - 1 ? "1px solid #e5e7eb" : "none",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      color: "#262626",
                    }}
                  >
                    Item {index + 1}
                  </Typography>
                  {draftItems.length > 1 ? (
                    <Button
                      size="small"
                      onClick={() => handleRemoveItem(item.id)}
                      startIcon={
                        <DeleteOutlineIcon sx={{ fontSize: "18px !important" }} />
                      }
                      sx={{
                        textTransform: "none",
                        fontWeight: 500,
                        fontSize: "0.875rem",
                        color: "#ef4444",
                        minWidth: "auto",
                        p: 0,
                        "&:hover": {
                          backgroundColor: "transparent",
                          color: "#dc2626",
                        },
                        "& .MuiButton-startIcon": {
                          mr: 0.5,
                          color: "inherit",
                        },
                      }}
                    >
                      Remove Item
                    </Button>
                  ) : null}
                </Box>

                <AssessmentPlanFilterFields
                  variant="form"
                  fullWidth
                  objectType={item.objectType}
                  objectSubType={item.objectSubType}
                  conceptCode={item.conceptCode}
                  errors={{
                    objectType: fieldErrors?.objectType,
                    objectSubType: fieldErrors?.objectSubType,
                    conceptCode: fieldErrors?.conceptCode,
                  }}
                  onObjectTypeChange={(nextObjectType, nextObjectSubType) =>
                    handleUpdateItem(item.id, {
                      objectType: nextObjectType,
                      objectSubType: nextObjectSubType,
                      conceptCode: "",
                      conceptValues: [],
                      assessmentPrompt: "",
                      allValues: false,
                    })
                  }
                  onObjectSubTypeChange={(nextObjectSubType) =>
                    handleUpdateItem(item.id, {
                      objectSubType: nextObjectSubType,
                      conceptCode: "",
                      conceptValues: [],
                      assessmentPrompt: "",
                      allValues: false,
                    })
                  }
                  onConceptCodeChange={(nextConceptCode) =>
                    handleUpdateItem(item.id, {
                      conceptCode: nextConceptCode,
                      conceptValues: [],
                      allValues: false,
                      assessmentPrompt: "",
                    })
                  }
                />

                {item.conceptCode ? (
                  <DefaultAssessmentPromptPrefetch
                    conceptCode={item.conceptCode}
                    objectType={item.objectType}
                    objectSubType={item.objectSubType}
                  />
                ) : null}

                {showConceptValues ? (
                  <ConceptValuesMultiSelect
                    key={`${item.id}-${item.conceptCode}`}
                    conceptCode={item.conceptCode}
                    conceptName={formatConceptCodeToLabel(item.conceptCode)}
                    value={item.conceptValues}
                    allValues={item.allValues ?? false}
                    error={Boolean(fieldErrors?.conceptValues)}
                    helperText={fieldErrors?.conceptValues}
                    onChange={({ conceptValues: nextValues, allValues: nextAllValues }) => {
                      handleUpdateItem(item.id, {
                        conceptValues: nextValues,
                        allValues: nextAllValues,
                        assessmentPrompt: "",
                      });
                    }}
                  />
                ) : null}

                {showAssessmentPrompt ? (
                  <AssessmentPromptFieldWithDefault
                    conceptCode={item.conceptCode}
                    objectType={item.objectType}
                    objectSubType={item.objectSubType}
                    value={item.assessmentPrompt}
                    error={Boolean(fieldErrors?.assessmentPrompt)}
                    helperText={fieldErrors?.assessmentPrompt}
                    maxLength={ASSESSMENT_PROMPT_MAX_LENGTH}
                    onChange={(nextValue) =>
                      handleUpdateItem(item.id, { assessmentPrompt: nextValue })
                    }
                  />
                ) : null}
              </Box>
            );
            })}

            <Button
              variant="outlined"
              fullWidth
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              onClick={handleAddOneItem}
              disabled={submitting}
              sx={{
                ...outlinedPrimarySx,
                height: 40,
                minHeight: 40,
                borderRadius: "6px",
              }}
            >
              Add One Item
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            flexShrink: 0,
            bgcolor: "#fff",
            px: "24px",
            py: "16px",
          }}
        >
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={submitting}
            sx={outlinedPrimarySx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSubmit()}
            disabled={submitting}
            startIcon={
              submitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            sx={{
              ...brandContainedButtonSx,
              bgcolor: `${primaryColor} !important`,
              color: `${primaryFontColor} !important`,
              minWidth: 88,
              "&:hover": {
                bgcolor: `${primaryColor} !important`,
              },
            }}
          >
            {submitting ? "Submitting…" : "Submit"}
          </Button>
        </Box>
      </Box>

      <ExtractFromFileDialog
        open={extractDialogOpen}
        confirming={importing}
        onClose={handleCloseExtractDialog}
        onConfirm={handleConfirmExtractFile}
      />
    </Box>
  );
}
